import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FileUpload } from 'tabler-icons-react';

import { CustomDropzone } from 'components/shared';
import { palletsToSendFileInputAcceptTypes } from 'constants/fileInputAcceptTypes';
import { usePalletsTypes } from 'constants/palletTypes';
import { SendPalletsItem } from 'interfaces';
import {
   handleTarczynskiClientSendPalletsDataFromExcel,
   handleVglClientSendPalletsDataFromExcel,
   TarczynskiClientExcelRow,
   VGLClientExcelRow,
} from 'service/excel';
import { uploadExcelFile } from 'utils/functions';

import styles from './PalletsToSendFileDropzone.module.css';

const MAX_SEND_PALLETS_EXCEL_ROWS = 1000;
const MAX_IMPORTED_SEND_PALLETS_ITEMS = 800;

type PalletsToSendFileDropzoneProps = {
   isVGLCompany: boolean;
   onStartUpload: () => void;
   onFinishUpload: () => void;
   onSuccessUpload: (sendPalletsItems: SendPalletsItem[]) => void;
   onErrorUpload: (error: unknown) => void;
};

const PalletsToSendFileDropzone = ({
   isVGLCompany,
   onStartUpload,
   onFinishUpload,
   onSuccessUpload,
   onErrorUpload,
}: PalletsToSendFileDropzoneProps) => {
   const { t } = useTranslation();

   const { palletTypesData } = usePalletsTypes();

   const validateImportedSendPalletsItems = useCallback(
      (sendPalletsItems: SendPalletsItem[]) => {
         if (sendPalletsItems.length > MAX_IMPORTED_SEND_PALLETS_ITEMS) {
            throw new Error(
               t('sendPalletsModal.errors.shipmentImportLimitExceeded', {
                  count: sendPalletsItems.length,
                  limit: MAX_IMPORTED_SEND_PALLETS_ITEMS,
               }),
            );
         }

         return sendPalletsItems;
      },
      [t],
   );

   const handleUploadExcelFile = useCallback(
      async (file: File) => {
         try {
            if (isVGLCompany) {
               const uploadedData = await uploadExcelFile<VGLClientExcelRow>(file, {
                  maxRows: MAX_SEND_PALLETS_EXCEL_ROWS,
               });
               onSuccessUpload(
                  validateImportedSendPalletsItems(
                     handleVglClientSendPalletsDataFromExcel(uploadedData),
                  ),
               );
               return;
            }
            // for other clients is used Tarczynski excel format
            const uploadedData = await uploadExcelFile<TarczynskiClientExcelRow>(file, {
               maxRows: MAX_SEND_PALLETS_EXCEL_ROWS,
            });
            onSuccessUpload(
               validateImportedSendPalletsItems(
                  handleTarczynskiClientSendPalletsDataFromExcel(uploadedData, palletTypesData),
               ),
            );
         } catch (error) {
            onErrorUpload(error);
         } finally {
            onFinishUpload();
         }
      },
      [
         isVGLCompany,
         onErrorUpload,
         onFinishUpload,
         onSuccessUpload,
         palletTypesData,
         validateImportedSendPalletsItems,
      ],
   );

   const onDrop = useCallback(
      (acceptedFiles: File[]) => {
         onStartUpload();
         const file = acceptedFiles[0];
         handleUploadExcelFile(file);
      },
      [handleUploadExcelFile, onStartUpload],
   );

   return (
      <CustomDropzone
         accept={palletsToSendFileInputAcceptTypes}
         maxFiles={1}
         multiple={false}
         noClick={true}
         onDrop={onDrop}
         inputProps={{
            text: t('sendPalletsModal.palletsToSendExcelDropzone.chooseFile'),
            className: styles.excelUploadBtn,
            icon: <FileUpload />,
         }}
         className={styles.dropzone}
      >
         <p
            className={styles.description}
            dangerouslySetInnerHTML={{
               __html: t('sendPalletsModal.palletsToSendExcelDropzone.description'),
            }}
         />
      </CustomDropzone>
   );
};

export default PalletsToSendFileDropzone;
